export const Logic = {
  AND: "and",
  OR: "or",
  NOT: "not",
  EQ: "eq",
  IN: "in"
};

type LogicType = "and" | "or" | "not" | "eq" | "in";

export interface BoolExpression {
  logic?: LogicType;
  expressions?: BoolExpression[];
  field?: string;
  value?: unknown;
}

/**
 * Evaluates a boolean expression.
 * @param expr - Boolean expression, boolean value, or function
 * @param values - Current field values
 * @returns Result of the evaluation
 *
 * @example
 * const expr = {
 *   logic: "and",
 *   expressions: [
 *     { logic: "eq", field: "age", value: 18 }
 *   ]
 * }
 *
 * const values = { age: 18, name: "Alice" }
 *
 * console.log(evaluateBool(expr, values)) // true
 */
export function evaluateBool(
  expr: boolean | BoolExpression | ((values: Record<string, unknown>) => boolean),
  values: Record<string, unknown>
): boolean {
  if (typeof expr === "boolean") return expr;
  if (typeof expr === "function") return expr(values);
  if (!expr) return true;

  switch (expr.logic) {
    case "and":
      return (expr.expressions ?? []).every((e: BoolExpression) => evaluateBool(e, values));
    case "or":
      return (expr.expressions ?? []).some((e: BoolExpression) => evaluateBool(e, values));
    case "not":
      return expr.expressions && expr.expressions[0]
        ? !evaluateBool(expr.expressions[0], values)
        : true;
    case "eq":
      return getVal(expr, values) === expr.value;
    case "in":
      return Array.isArray(expr.value)
        ? (expr.value as unknown[]).includes(getVal(expr, values))
        : false;
    default:
      return true;
  }
}

/**
 * Gets the value of a field from the expression.
 * @param expr - Boolean expression
 * @param values - Current field values
 * @returns Value of the field
 *
 * @example
 * const expr = { field: "age" }
 * const values = { age: 20 }
 * console.log(getVal(expr, values)) // 20
 */
function getVal(expr: Pick<BoolExpression, "field">, values: Record<string, unknown>) {
  if (expr.field) {
    return values[expr.field];
  }
  return undefined;
}
